import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import { Divider } from '@mui/material';

import { categoriesForCoach, categoryMap } from '../../../../../../../utils/userCategory';
import { getContractEntries } from '../../../../../../../utils/userCoachData';
import Community, { GENERIC_COMMUNITY } from '../../../../../../Model/Community';
import Product from '../../../../../../../Model/Product';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import AnnouncementContext from '../../../../../../context/AnnouncementContext';
import ExternalCoachContext from '../../../../../../context/ExternalCoachContext';
import PrimaryButton from '../../../../../../../components/Button/ActionButtons/PrimaryButton';
import { DarkTag } from '../../../../../../../components/Tags';
import {
  InfoContainer,
  StyledTitle,
  StyledSubTitle,
  StyledAccordion,
  StyledArrowIcon,
  SectionContent,
  ChipContainer,
  SectionActionWrapper,
} from '../../styles';
import { accordionId } from '../../utils';

import EditCommunitiesModal from '../EditCommunitiesModal';
import { getGenericCommunities } from './utils';
import { StyledEditIcon } from './styles';
import texts from './texts.json';

const { COMMUNITIES_ACCORDION } = accordionId;

const CommunitiesSection = ({
  userDoc,
  communityIds,
  handleExpand,
  expanded,
  isQAP,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unlistedCommunities, setUnlistedCommunities] = useState([]);
  const [otherCoachCommunities, setOtherCoachCommunities] = useState([]);
  const [productCommunityIds, setProductCommunityIds] = useState([]);
  const [areGrantsResolved, setAreGrantsResolved] = useState(false);

  const isComponentMountedRef = useComponentMounted();
  const { communities: coachCommunities = [], isReady } = useContext(AnnouncementContext);
  const { products, coachDoc } = useContext(ExternalCoachContext);
  const viewingCoachId = coachDoc?.id;

  // `communityAccessFor` builds a new object every render; keep one per content so the memos here and in the
  // modal only recompute when the access changes.
  const latestAccess = userDoc.communityAccessFor(viewingCoachId);
  const accessKey = latestAccess
    ? JSON.stringify([
      latestAccess.defaultCommunityId,
      Object.entries(latestAccess.frozen).map(([id, frozenAt]) => [id, frozenAt?.toMillis?.() ?? null]),
    ])
    : '';
  const accessRef = useRef({ key: accessKey, value: latestAccess });
  if (accessRef.current.key !== accessKey) {
    accessRef.current = { key: accessKey, value: latestAccess };
  }
  const communityAccess = accessRef.current.value;
  const frozenIdsKey = Object.keys(communityAccess?.frozen || {}).join(',');

  // Granted ids plus frozen ones.
  const heldIds = useMemo(
    () => [...new Set([...communityIds, ...(frozenIdsKey ? frozenIdsKey.split(',') : [])])],
    [communityIds, frozenIdsKey],
  );

  // Granted ids the viewing coach's own list can't explain. Each is resolved by id and then split
  // on its `coach`: another live coach's community, which this coach cannot revoke, or a genuinely
  // unlisted one (archived, or kept from a coach the client no longer holds). Joined into a string
  // so the effect below keys off the contents: the parent re-renders on every accordion click and
  // hands down a fresh array each time.
  const unlistedIdsKey = useMemo(() => {
    const listed = new Set([...GENERIC_COMMUNITY, ...coachCommunities].map((community) => community.id));
    return heldIds.filter((id) => !listed.has(id)).join(',');
  }, [heldIds, coachCommunities]);

  // Every product the client holds with this coach, else the legacy root product.
  const productIds = userDoc.hasContractEntries
    ? getContractEntries(userDoc.data.contracts, viewingCoachId).map((contract) => contract.productId)
    : [userDoc.product];
  const productIdsKey = [...new Set(productIds.filter(Boolean))].join(',');
  const clientCoachIdsKey = userDoc.activeCoachIds.join(',');

  useEffect(() => {
    const resolveGrants = async () => {
      setAreGrantsResolved(false);
      const unlistedIds = unlistedIdsKey ? unlistedIdsKey.split(',') : [];
      const contractProductIds = productIdsKey ? productIdsKey.split(',') : [];
      try {
        const [unlistedDocs, productDocs] = await Promise.all([
          Promise.all(unlistedIds.map((id) => Community.getById(id))),
          Promise.all(contractProductIds.map((id) => Product.getById(id))),
        ]);
        if (!isComponentMountedRef.current) {
          return;
        }
        const resolved = unlistedDocs.filter(Boolean).map((community) => ({
          id: community.id,
          name: community.name,
          isArchived: community.isArchived,
          coach: community.coach,
        }));
        /*
          Another coach's only when that coach is one the client currently holds — those are
          theirs to manage. `coachIds` never shrinks, so an ended relationship has to be filtered
          out by activeness: its communities belong in the revocable branch below.
        */
        const clientCoaches = clientCoachIdsKey ? clientCoachIdsKey.split(',') : [];
        const isAnotherCoachs = (community) => !!community.coach
          && community.coach !== viewingCoachId
          && clientCoaches.includes(community.coach);
        setOtherCoachCommunities(resolved.filter(isAnotherCoachs));
        setUnlistedCommunities(resolved.filter((community) => !isAnotherCoachs(community)));
        setProductCommunityIds([...new Set(productDocs.map((productDoc) => productDoc?.communityId).filter(Boolean))]);
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        if (isComponentMountedRef.current) {
          setAreGrantsResolved(true);
        }
      }
    };
    resolveGrants();
  }, [unlistedIdsKey, productIdsKey, viewingCoachId, clientCoachIdsKey, isComponentMountedRef]);

  const nameById = useMemo(
    () => [...GENERIC_COMMUNITY, ...coachCommunities, ...otherCoachCommunities, ...unlistedCommunities]
      .reduce((acc, community) => {
        acc[community.id] = community.name;
        return acc;
      }, {}),
    [coachCommunities, otherCoachCommunities, unlistedCommunities],
  );

  // One generic per category held: a client on a challenge and a 1:1 belongs to both.
  const hasCommunityAccess = !!communityAccess;
  const genericCommunities = useMemo(() => {
    const byProduct = categoryMap(products);
    const categories = hasCommunityAccess
      ? new Set((productIdsKey ? productIdsKey.split(',') : []).map((id) => byProduct.get(id)).filter(Boolean))
      : categoriesForCoach(byProduct, userDoc, viewingCoachId);
    const frozenIds = frozenIdsKey ? frozenIdsKey.split(',') : [];
    const frozen = GENERIC_COMMUNITY.filter((community) => frozenIds.includes(community.id));
    const held = [...getGenericCommunities(categories), ...frozen];
    return [...new Map(held.map((community) => [community.id, community])).values()];
  }, [products, userDoc, viewingCoachId, hasCommunityAccess, productIdsKey, frozenIdsKey]);

  const clientCommunities = useMemo(() => {
    const resolved = heldIds.map((id) => ({ id, name: nameById[id] })).filter((community) => community.name);
    // A generic can legitimately already be in communityIds, so dedupe on id.
    const byId = new Map([...genericCommunities, ...resolved].map((community) => [community.id, community]));
    return [...byId.values()];
  }, [heldIds, nameById, genericCommunities]);

  const showActions = expanded === COMMUNITIES_ACCORDION || !isQAP;
  const isResolvingCommunities = !isReady || !areGrantsResolved;

  return (
    <>
      <Divider flexItem />
      <StyledAccordion
        key={texts.communities}
        title={<StyledTitle>{texts.communities}</StyledTitle>}
        expanded={showActions}
        onChange={handleExpand(COMMUNITIES_ACCORDION)}
        expandIcon={isQAP && <StyledArrowIcon />}
        preventRotation={!isQAP}
        disableGutters
        actions={
          showActions && (
            <SectionActionWrapper $isQAP={isQAP} onClick={(e) => e.stopPropagation()}>
              <PrimaryButton
                onClick={() => setIsModalOpen(true)}
                variant="info"
                size="medium"
                disabled={isResolvingCommunities}
              >
                <StyledEditIcon />
                {texts.edit}
              </PrimaryButton>
            </SectionActionWrapper>
          )
        }
      >
        <InfoContainer>
          <SectionContent>
            {isResolvingCommunities ? (
              <StyledSubTitle>
                {texts.loading}
              </StyledSubTitle>
            ) : (
              <ChipContainer>
                {clientCommunities.map((community) => (
                  <DarkTag key={community.id}>{community.name}</DarkTag>
                ))}
              </ChipContainer>
            )}
          </SectionContent>
        </InfoContainer>
      </StyledAccordion>
      {isModalOpen && (
        <EditCommunitiesModal
          isOpen={isModalOpen}
          onDidDismiss={() => setIsModalOpen(false)}
          userDoc={userDoc}
          coachCommunities={coachCommunities}
          otherCoachCommunities={otherCoachCommunities}
          unlistedCommunities={unlistedCommunities}
          productCommunityIds={productCommunityIds}
          grantedIds={communityIds}
          genericCommunities={genericCommunities}
          communityAccess={communityAccess}
        />
      )}
    </>
  );
};

CommunitiesSection.propTypes = {
  userDoc: PropTypes.object.isRequired,
  communityIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleExpand: PropTypes.func.isRequired,
  expanded: PropTypes.string.isRequired,
  isQAP: PropTypes.bool.isRequired,
};

CommunitiesSection.defaultProps = {
};

export default CommunitiesSection;
